import {useLayoutEffect, useRef, useState} from 'react';

import {Link as RouterLink} from 'react-router-dom';

import {Box, Grid, SxProps, Typography} from '@mui/material';
import {Variant} from '@mui/material/styles/createTypography';
import {TypographyPropsVariantOverrides} from '@mui/material/Typography/Typography';
import {OverridableStringUnion} from '@mui/types';

import {EmpionTooltip} from '../EmpionTooltip/EmpionTooltip';

import {greenColors} from '../../layouts/theme/designSystem';

interface Props {
  variant: OverridableStringUnion<Variant | 'inherit', TypographyPropsVariantOverrides>;
  text: string;
  sx?: SxProps;
  sxTypography?: SxProps;
  copyToClipboard?: (text: string) => void;
  /** When set, text renders as a client-side link (ellipsis + tooltip unchanged). */
  to?: string;
}

const linkTypographySx = {
  color: greenColors[700],
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: greenColors[600],
  },
};

export const TextOverflowWithTooltip = ({variant, text, sx, sxTypography, copyToClipboard, to}: Props) => {
  const ref = useRef<HTMLElement>(null);
  const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateOverflow = () => {
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      setIsTooltipEnabled(overflowing);
    };

    updateOverflow();
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, to]);

  const typographySx = {
    display: 'block',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...sxTypography,
    ...(to ? linkTypographySx : {}),
  };

  const typographyProps = {
    ref,
    variant,
    onClick: copyToClipboard && !to ? () => copyToClipboard(text) : undefined,
    ...(to ? {component: RouterLink, to} : {}),
  };

  return (
    <Grid overflow='hidden' sx={{minWidth: 0, maxWidth: '100%', flex: '1 1 auto', ...sx}}>
      <EmpionTooltip title={text} isEnabled={isTooltipEnabled}>
        <Box component='span' sx={{display: 'block', minWidth: 0, width: '100%'}}>
          <Typography {...typographyProps} sx={typographySx}>
            {text}
          </Typography>
        </Box>
      </EmpionTooltip>
    </Grid>
  );
};
